import { useEffect, useRef, useState } from 'react';

interface PromptDialogProps {
  isOpen: boolean;
  /** Scene name shown in the title so the reviewer knows which crop is edited. */
  scene: string | null;
  onSubmit: (prompt: string) => void;
  onCancel: () => void;
  isLoading: boolean;
}

/**
 * Quick-fill templates for the "Prompt" action. Picking one drops its
 * text into the editable textarea (the reviewer can then tweak it). The
 * last entry is intentionally open-ended so the reviewer appends the
 * target colour.
 */
const PREDEFINED_PROMPTS: string[] = [
  'Undistort the image',
  "Rotate the wheel so it's straight, do not change anything else in the image",
  'Change the side mirror color to ',
];

/**
 * Free-text prompt collector for the per-scene "Prompt" action. Reuses
 * the `ConfirmDialog` modal shell from `HistoricalTable` (same backdrop /
 * card / footer-button layout, `variant='primary'` styling) with a
 * `<textarea>` body. The Confirm button stays disabled until the
 * reviewer types a non-empty instruction.
 */
export function PromptDialog({
  isOpen,
  scene,
  onSubmit,
  onCancel,
  isLoading,
}: PromptDialogProps) {
  const [prompt, setPrompt] = useState('');
  const [preset, setPreset] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset the field each time the dialog reopens (possibly for a
  // different scene) and focus the textarea for immediate typing.
  useEffect(() => {
    if (isOpen) {
      setPrompt('');
      setPreset('');
      // Defer focus until after the element is painted.
      const id = window.setTimeout(() => textareaRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const trimmed = prompt.trim();
  const canSubmit = trimmed.length > 0 && !isLoading;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(trimmed);
  };

  // Fill the textarea from a chosen template, leaving it fully editable.
  // The <select> resets to its placeholder so it stays a one-shot picker
  // and never misrepresents an edited prompt.
  const handlePresetChange = (value: string) => {
    if (!value) return;
    setPrompt(value);
    setPreset('');
    window.setTimeout(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      // Park the cursor at the end so open-ended templates (e.g. the
      // side-mirror colour) can be completed straight away.
      el.setSelectionRange(value.length, value.length);
    }, 0);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={isLoading ? undefined : onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Re-prompt {scene ? <span className="capitalize">{scene.replace(/_/g, ' ')}</span> : 'scene'}
        </h3>
        <p className="text-slate-600 mb-4 text-sm">
          Describe the change to apply to the current crop. The edit runs on
          the image you see now.
        </p>
        <label className="block text-xs font-medium text-slate-500 mb-1">
          Predefined prompts
        </label>
        <select
          value={preset}
          onChange={(e) => handlePresetChange(e.target.value)}
          disabled={isLoading}
          className="input-pill w-full mb-3 disabled:opacity-50"
        >
          <option value="">Select a predefined prompt…</option>
          {PREDEFINED_PROMPTS.map((p) => (
            <option key={p} value={p}>
              {p.trim()}
            </option>
          ))}
        </select>
        <textarea
          ref={textareaRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            // Cmd/Ctrl+Enter submits as a convenience.
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              handleSubmit();
            }
          }}
          disabled={isLoading}
          rows={4}
          placeholder="e.g. remove the reflection on the dashboard"
          className="input-pill w-full resize-y mb-6 disabled:opacity-50"
        />
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-500 hover:opacity-95 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            Apply prompt
          </button>
        </div>
      </div>
    </div>
  );
}
